import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Tag, Share2, Twitter, Linkedin, Facebook } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Header from './Header';
import Footer from './Footer';
import { blogs, blogContent } from '../mock';
import SEO from './SEO';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const blog = blogs.find(b => b.slug === slug);
  const content = blogContent[slug];

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Blog Not Found</h1>
          <Button onClick={() => navigate('/')}>Go Back Home</Button>
        </div>
      </div>
    );
  }

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = blog.title;

    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    };

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  return (
    <>
      <SEO
        title={blog.title}
        description={blog.excerpt || blog.description}
        keywords={`${blog.title}, ${blog.category}, web development blog, React tutorial, Node.js guide, CodingSeagull blog, Gokul Krishnan blog`}
        canonicalUrl={`/blog/${slug}`}
        ogImage={blog.image || '/og-image.jpg'}
        type="article"
        author="Gokul Krishnan Koyattassery"
      />

      {/* Article Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": blog.title,
          "description": blog.excerpt || blog.description,
          "image": blog.image ? `https://codingseagull.com${blog.image}` : "https://codingseagull.com/og-image.jpg",
          "author": {
            "@type": "Person",
            "name": "Gokul Krishnan Koyattassery",
            "url": "https://codingseagull.com"
          },
          "publisher": {
            "@type": "Organization",
            "name": "CodingSeagull",
            "logo": {
              "@type": "ImageObject",
              "url": "https://codingseagull.com/icon2.svg"
            }
          },
          "datePublished": blog.date || blog.publishedDate,
          "dateModified": blog.modifiedDate || blog.date || blog.publishedDate,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://codingseagull.com/blog/${slug}`
          }
        })}
      </script>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Header />

        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-cyan-600 to-blue-700 text-white">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="relative max-w-4xl mx-auto px-6 py-20">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-white hover:text-white hover:bg-white/20 mb-6"
            >
              <ArrowLeft className="mr-2" size={20} />
              Back to Home
            </Button>

            <div className="flex flex-wrap gap-2 mb-6">
              {blog.tags.map((tag, index) => (
                <Badge
                  key={index}
                  className="bg-white/20 text-white border-0 backdrop-blur-sm"
                >
                  {tag}
                </Badge>
              ))}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {blog.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>{new Date(blog.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} />
                <span>{blog.readTime}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        <div className="max-w-5xl mx-auto px-6 -mt-10">
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img
              src={blog.image}
              alt={blog.title}
              className="w-full h-[400px] object-cover mt-10"
            />
          </div>
        </div>

        {/* Main Content */}
        <article className="max-w-4xl mx-auto px-6 py-16">
          {/* Share Buttons - Sticky */}
          <div className="hidden lg:block fixed left-8 top-1/2 transform -translate-y-1/2">
            <div className="flex flex-col gap-4 bg-white p-3 rounded-full shadow-lg">
              <button
                onClick={() => handleShare('twitter')}
                className="p-3 hover:bg-cyan-50 rounded-full transition-colors group"
                aria-label="Share on Twitter"
              >
                <Twitter size={20} className="text-gray-600 group-hover:text-cyan-600" />
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="p-3 hover:bg-blue-50 rounded-full transition-colors group"
                aria-label="Share on LinkedIn"
              >
                <Linkedin size={20} className="text-gray-600 group-hover:text-blue-600" />
              </button>
              <button
                onClick={() => handleShare('facebook')}
                className="p-3 hover:bg-blue-50 rounded-full transition-colors group"
                aria-label="Share on Facebook"
              >
                <Facebook size={20} className="text-gray-600 group-hover:text-blue-700" />
              </button>
            </div>
          </div>

          {/* Mobile Share Buttons */}
          <div className="lg:hidden flex justify-center gap-4 mb-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleShare('twitter')}
            >
              <Twitter size={16} className="mr-2" />
              Share
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleShare('linkedin')}
            >
              <Linkedin size={16} className="mr-2" />
              Share
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleShare('facebook')}
            >
              <Facebook size={16} className="mr-2" />
              Share
            </Button>
          </div>

          {/* Blog Content */}
          <div className="prose prose-lg max-w-none">
            {content ? (
              <>
                {content.introduction && (
                  <div className="bg-cyan-50 border-l-4 border-cyan-600 p-6 rounded-r-lg mb-8">
                    <p className="text-lg text-gray-700 leading-relaxed italic">
                      {content.introduction}
                    </p>
                  </div>
                )}

                {content.sections.map((section, index) => (
                  <div key={index} className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">
                      {section.heading}
                    </h2>

                    {section.content.map((paragraph, pIndex) => (
                      <p key={pIndex} className="text-gray-700 leading-relaxed mb-4 text-lg">
                        {paragraph}
                      </p>
                    ))}

                    {section.list && (
                      <ul className="space-y-3 my-6">
                        {section.list.map((item, lIndex) => (
                          <li key={lIndex} className="flex items-start">
                            <span className="inline-block w-2 h-2 bg-cyan-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                            <span className="text-gray-700 text-lg">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {section.code && (
                      <div className="bg-gray-900 rounded-lg p-6 my-6 overflow-x-auto">
                        <pre className="text-gray-100 text-sm">
                          <code>{section.code}</code>
                        </pre>
                      </div>
                    )}
                  </div>
                ))}

                {content.conclusion && (
                  <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-8 rounded-xl mt-12 border border-cyan-100">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Conclusion</h2>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      {content.conclusion}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-gray-700 leading-relaxed space-y-6">
                <p className="text-lg">{blog.excerpt}</p>
                <p className="text-lg">
                  This is a placeholder for the full blog content. The actual content would contain
                  detailed information, code examples, and in-depth explanations about {blog.title}.
                </p>
              </div>
            )}
          </div>

          {/* Tags Section */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag size={20} className="text-gray-500" />
              <span className="text-gray-700 font-semibold">Tags:</span>
              {blog.tags.map((tag, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="bg-cyan-100 text-cyan-800 hover:bg-cyan-200"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Related blogs */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">More Articles</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {blogs
                .filter(b => b.id !== blog.id)
                .slice(0, 2)
                .map((relatedBlog) => (
                  <div
                    key={relatedBlog.id}
                    onClick={() => navigate(`/blog/${relatedBlog.slug}`)}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                  >
                    <img
                      src={relatedBlog.image}
                      alt={relatedBlog.title}
                      className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="p-4">
                      <h4 className="font-bold text-gray-900 mb-2 group-hover:text-cyan-600 transition-colors">
                        {relatedBlog.title}
                      </h4>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {relatedBlog.excerpt}
                      </p>
                      <div className="flex items-center text-xs text-gray-500">
                        <Calendar size={14} className="mr-1" />
                        <span>{new Date(relatedBlog.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}</span>
                        <span className="mx-2">•</span>
                        <Clock size={14} className="mr-1" />
                        <span>{relatedBlog.readTime}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
};

export default BlogPost;